import type { UseFormReturn } from "react-hook-form";

import type { ArtworkForm } from "@design-lab/shared/types";
import type { CachedImage } from "../model/useImagesFastCache";
import { calculatedArtworkDimensions, type Surface } from "./calculateDimensions";

type Surfaces = {
  front?: Surface;
  back?: Surface;
  frontLeftChest?: Surface;
};

type SetArtworkDimensionsParams = {
  form: UseFormReturn<ArtworkForm>;
  side: "front" | "back";
  cachedImage: CachedImage | undefined;
  surfaces: Surfaces | undefined;
};

export function setArtworkDimensions({
  form,
  side,
  cachedImage,
  surfaces
}: SetArtworkDimensionsParams) {
  if (side === "back") {
    const backSurface = surfaces?.[side];
    const artworkDimensions = calculatedArtworkDimensions(cachedImage?.image, backSurface);

    form.setValue(`${side}.size`, {
      width: artworkDimensions.maxWidthInches,
      height: artworkDimensions.maxHeightInches
    });
  } else {
    const frontArtworkDimensions = calculatedArtworkDimensions(cachedImage?.image, surfaces?.front);
    form.setValue(`front.size`, {
      width: frontArtworkDimensions.maxWidthInches,
      height: frontArtworkDimensions.maxHeightInches
    });

    const frontLeftChestArtworkDimensions = calculatedArtworkDimensions(
      cachedImage?.image,
      surfaces?.frontLeftChest
    );
    form.setValue(`frontLeftChest.size`, {
      width: frontLeftChestArtworkDimensions.maxWidthInches,
      height: frontLeftChestArtworkDimensions.maxHeightInches
    });
  }
}

export function setArtworkId(
  form: UseFormReturn<ArtworkForm>,
  side: "front" | "back",
  artworkId: string
) {
  if (side === "back") {
    form.setValue("back.artworkId", artworkId);
  } else {
    form.setValue("front.artworkId", artworkId);
    form.setValue("frontLeftChest.artworkId", artworkId);
  }
}

export function clearArtworkFormValues(form: UseFormReturn<ArtworkForm>, placement: string) {
  if (placement === "back") {
    form.setValue("back.artworkId", "");
    form.setValue("back.llmRedrawRequestId", undefined);
  } else {
    form.setValue("front.artworkId", "");
    form.setValue("frontLeftChest.artworkId", "");
    form.setValue("front.llmRedrawRequestId", undefined);
    form.setValue("frontLeftChest.llmRedrawRequestId", undefined);
  }

  const infoPlacement: "front" | "back" = placement === "back" ? "back" : "front";
  form.setValue(`info.${infoPlacement}.enhancementRequestNote`, "");
  form.setValue(`info.${infoPlacement}.enhancementTier`, "full_enhancements");
  form.setValue(`info.${infoPlacement}.removeBackground`, true);
  form.setValue(`info.${infoPlacement}.jiffyDesignServices`, false);
  form.setValue(`info.${infoPlacement}.editImageManual`, false);
}
